import React, { useState, useEffect, useRef, useCallback } from "react";

const norrisMagoImg =
  "https://res.cloudinary.com/dbvqeiqxb/image/upload/v1783721880/norris-mago-2_gfv22t.png";

const STAR_TILE_SIZE = 180;
const STAR_DOTS = [
  [5, 15, 1.2, 0.4],
  [22, 48, 0.8, 0.3],
  [40, 10, 1, 0.35],
  [55, 70, 1.4, 0.45],
  [70, 25, 0.7, 0.25],
  [88, 55, 1.1, 0.4],
  [100, 8, 0.9, 0.3],
  [115, 90, 1.3, 0.5],
  [130, 35, 0.8, 0.28],
  [150, 65, 1, 0.38],
  [160, 15, 1.2, 0.42],
  [15, 100, 0.9, 0.3],
  [35, 130, 1.1, 0.35],
  [60, 110, 0.7, 0.25],
  [80, 145, 1.3, 0.45],
  [95, 120, 0.8, 0.3],
  [120, 155, 1, 0.4],
  [140, 110, 0.9, 0.32],
  [170, 135, 1.1, 0.38],
  [10, 165, 0.8, 0.3],
];
const STARS_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="${STAR_TILE_SIZE}" height="${STAR_TILE_SIZE}">${STAR_DOTS.map(
  ([x, y, r, o]) =>
    `<circle cx="${x}" cy="${y}" r="${r}" fill="rgba(196,181,253,${o})"/>`
).join("")}</svg>`;
const STARS_BG_URL = `url("data:image/svg+xml,${encodeURIComponent(
  STARS_SVG
)}")`;

const RESPONSES = [
  "Obvio sí, bb",
  "Tú dale",
  "Date, hermosa",
  "Ni le pienses",
  "Hazlo por la anécdota",
  "Confía en ti, bb",
  "Confía, hermosa",
  "¿Qué más es posible?",
  "Todo llega con facilidad",
  "La práctica vence al miedo",
  "Es mi sueño, incluso mi ilusión",
  "Pregúntame después, bb",
  "Ay, bb... quién sabe",
  "Mejor haz otra preguntita",
  "Interesante punto de vista",
  "¿Tú qué crees, bb?",
  "Que no cunda el pánico",
  "Espérate tantito",
  "Todavía no lo veo claro",
  "Primero piensa bien, bb",
  "Nombre, pa' qué",
  "Uy... no creo, bb",
  "Esta vez no, hermosa",
  "No me convence",
  "Mejor no, bb",
  "Yo ahí no me metería",
  "Nel, bb",
];

const BASE_TOP_W = 48;
const BASE_HEIGHT = 50;
const CENTER_Y = 63;
const CX = 62;
const FRACS_BY_COUNT = { 1: [0.34], 2: [0.2, 0.32], 3: [0.11, 0.23, 0.34] };
const CHAR_W = 4.4;
const PAD = 7;
const MIN_S = 0.85;
const MAX_S = 1.7;
const BUFFER = 1.05;
const LINE_PENALTY = 0.22;
const MIN_LINE_GAP = 7;

// Tries every way to split the phrase into 1, 2 or 3 lines and keeps
// whichever grouping needs the least triangle scale-up to fit — this is
// what lets long, uneven phrases (varying word counts) still land cleanly
// instead of dumping overflow words onto a fixed last line.
function lineFitScore(lineArr) {
  const fracs = FRACS_BY_COUNT[lineArr.length];
  if (!fracs) return Infinity;
  let maxRatio = 0;
  lineArr.forEach((line, i) => {
    const avail = BASE_TOP_W * (1 - fracs[i]);
    const needed = line.length * CHAR_W + PAD;
    const ratio = needed / avail;
    if (ratio > maxRatio) maxRatio = ratio;
  });
  return maxRatio;
}

function splitOptions(words) {
  const n = words.length;
  const options = [[words.join(" ")]];
  for (let i = 1; i < n; i++) {
    options.push([words.slice(0, i).join(" "), words.slice(i).join(" ")]);
  }
  for (let i = 1; i < n - 1; i++) {
    for (let j = i + 1; j < n; j++) {
      options.push([
        words.slice(0, i).join(" "),
        words.slice(i, j).join(" "),
        words.slice(j).join(" "),
      ]);
    }
  }
  return options;
}

function wrapLines(str) {
  const words = str.split(" ");
  const options = splitOptions(words);
  // Picking the option with the flat-out lowest ratio always wins by
  // fragmenting into as many lines as possible (more/shorter lines = lower
  // ratio), which is how short phrases like "Mejor no, bb" ended up crammed
  // into 3 tiny lines. Instead pick whichever option lands closest to a
  // ratio of 1 — a comfortable, non-scaled fit — among those that still fit
  // under MAX_S, with a small penalty per extra line so 2 lines only lose
  // to 3 when 3 genuinely fits noticeably better (not just marginally).
  const scored = options.map((opt) => ({ opt, ratio: lineFitScore(opt) }));
  const feasible = scored.filter((s) => s.ratio * BUFFER <= MAX_S);
  const pool = feasible.length ? feasible : scored;
  const penalizedScore = (s) =>
    Math.abs(s.ratio - 1) + (s.opt.length - 1) * LINE_PENALTY;
  let best = pool[0];
  let bestScore = penalizedScore(best);
  for (const s of pool) {
    const score = penalizedScore(s);
    if (score < bestScore) {
      bestScore = score;
      best = s;
    }
  }

  // Some 3-word phrases score just well enough as 3 tight lines to win on
  // paper, but the actual pixel gap between lines ends up too cramped to
  // read comfortably. When that happens, fall back to the best 2-line split.
  if (best.opt.length === 3) {
    const reqS = Math.min(MAX_S, Math.max(MIN_S, best.ratio * BUFFER));
    const fracs = FRACS_BY_COUNT[3];
    const gap =
      Math.min(fracs[1] - fracs[0], fracs[2] - fracs[1]) * BASE_HEIGHT * reqS;
    if (gap < MIN_LINE_GAP) {
      const twoLineOptions = feasible.filter((s) => s.opt.length === 2);
      if (twoLineOptions.length) {
        best = twoLineOptions.reduce((a, b) => (b.ratio < a.ratio ? b : a));
      }
    }
  }

  return best.opt.map((l) => l.toUpperCase());
}

function computeGeometry(lines) {
  const fracs = FRACS_BY_COUNT[lines.length];
  let s = 1;
  lines.forEach((line, i) => {
    const availAtS1 = BASE_TOP_W * (1 - fracs[i]);
    const needed = line.length * CHAR_W + PAD;
    const reqS = (needed / availAtS1) * BUFFER;
    if (reqS > s) s = reqS;
  });
  s = Math.min(MAX_S, Math.max(MIN_S, s));
  const topW = BASE_TOP_W * s;
  const height = BASE_HEIGHT * s;
  const topY = CENTER_Y - height / 2;
  const apexY = CENTER_Y + height / 2;
  const points = `${CX - topW / 2},${topY} ${
    CX + topW / 2
  },${topY} ${CX},${apexY}`;
  const ys = fracs.map((f) => topY + f * height);
  return { points, ys };
}

const SHAKE_THRESHOLD = 16;
const SHAKE_COOLDOWN_MS = 1500;

const TAP_REVEAL_MS = 1100;
const SHAKE_REVEAL_MS = 1600;
const TAP_BALL_ANIMATION = "ballshake 0.6s ease-in-out";
const SHAKE_BALL_ANIMATION = "ballshake 0.6s ease-in-out 3";

const MagicBall = () => {
  const [phase, setPhase] = useState("idle"); // idle | shaking | answer
  const [source, setSource] = useState("tap"); // which gesture triggered the current/last roll
  const [lines, setLines] = useState([]);
  const [geometry, setGeometry] = useState(null);
  const phaseRef = useRef("idle");

  // "checking"    → probing whether motion events fire (iOS, on mount)
  // "idle"        → needs the tap button (iOS, no events received)
  // "asking"      → requestPermission() in flight
  // "granted"     → sensor active
  // "denied"      → sensor blocked
  // "unavailable" → DeviceMotionEvent doesn't exist (desktop, etc.)
  const [iosPermission, setIosPermission] = useState(() => {
    if (typeof DeviceMotionEvent === "undefined") return "unavailable";
    if (typeof DeviceMotionEvent.requestPermission !== "function")
      return "granted";
    return "checking";
  });

  const roll = useCallback((src = "tap") => {
    const current = phaseRef.current;
    if (current === "shaking") return;
    if (current === "answer" && src === "tap") {
      phaseRef.current = "idle";
      setPhase("idle");
      return;
    }
    phaseRef.current = "shaking";
    setPhase("shaking");
    setSource(src);
    const delay = src === "shake" ? SHAKE_REVEAL_MS : TAP_REVEAL_MS;
    setTimeout(() => {
      const text = RESPONSES[Math.floor(Math.random() * RESPONSES.length)];
      const wrapped = wrapLines(text);
      setLines(wrapped);
      setGeometry(computeGeometry(wrapped));
      phaseRef.current = "answer";
      setPhase("answer");
    }, delay);
  }, []);

  const requestIOSPermission = async () => {
    if (typeof DeviceMotionEvent?.requestPermission !== "function") return;
    setIosPermission("asking");
    try {
      const result = await DeviceMotionEvent.requestPermission();
      setIosPermission(result === "granted" ? "granted" : "denied");
    } catch {
      setIosPermission("denied");
    }
  };

  // Probe: if motion events already fire (Android, or iOS granted earlier), skip the button
  useEffect(() => {
    if (iosPermission !== "checking") return;
    let receivedEvent = false;
    let cancelled = false;

    const probe = (e) => {
      if (e.accelerationIncludingGravity?.x != null) receivedEvent = true;
    };
    window.addEventListener("devicemotion", probe);

    const timeout = setTimeout(() => {
      if (cancelled) return;
      window.removeEventListener("devicemotion", probe);
      setIosPermission(receivedEvent ? "granted" : "idle");
    }, 500);

    return () => {
      cancelled = true;
      clearTimeout(timeout);
      window.removeEventListener("devicemotion", probe);
    };
  }, [iosPermission]);

  // Shake detection — rolls the ball with a longer reveal delay than a tap,
  // giving the person time to stop moving the phone before the answer shows.
  useEffect(() => {
    if (iosPermission !== "granted") return;

    const lastAccelRef = { current: null };
    let lastShakeAt = 0;

    const handler = (e) => {
      const acc = e.accelerationIncludingGravity;
      if (!acc || acc.x == null) return;
      const { x, y, z } = acc;
      const last = lastAccelRef.current;
      lastAccelRef.current = { x, y, z };
      if (!last) return;

      const delta =
        Math.abs(x - last.x) + Math.abs(y - last.y) + Math.abs(z - last.z);
      if (delta > SHAKE_THRESHOLD) {
        // Block only while the ball is actively shaking/animating — a shake
        // while an answer is showing is allowed and rolls a fresh one.
        if (phaseRef.current === "shaking") return;
        const now = Date.now();
        if (now - lastShakeAt < SHAKE_COOLDOWN_MS) return;
        lastShakeAt = now;
        if (navigator.vibrate) navigator.vibrate(20);
        roll("shake");
      }
    };

    window.addEventListener("devicemotion", handler, true);
    return () => window.removeEventListener("devicemotion", handler, true);
  }, [iosPermission, roll]);

  return (
    <div
      className="flex flex-col items-center font-quicksand"
      style={{
        backgroundColor: "#1c1030",
        backgroundImage: STARS_BG_URL,
        backgroundRepeat: "repeat",
        backgroundSize: `${STAR_TILE_SIZE}px ${STAR_TILE_SIZE}px`,
        minHeight: "calc(100vh - 120px)",
        paddingTop: "16px",
        paddingBottom: "32px",
        justifyContent: "center",
      }}
    >
      <div style={{ textAlign: "center", padding: "0 24px" }}>
        <p style={{ fontSize: "19px", color: "#e4dbff", margin: "0 0 4px" }}>
          piensa tu pregunta
        </p>
        <p style={{ fontSize: "15px", color: "#b3a6d9", margin: 0 }}>
          a ver qué responde, bb
        </p>
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginTop: "50px",
          marginBottom: "30px",
        }}
      >
        <div
          onClick={() => roll("tap")}
          role="button"
          tabIndex={0}
          aria-label={
            phase === "answer" ? "Ver otra pregunta" : "Agitar la bola"
          }
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") roll("tap");
          }}
          style={{
            width: "336px",
            height: "336px",
            borderRadius: "50%",
            background: "#0c0c10",
            position: "relative",
            border: "3px solid #303038",
            boxSizing: "border-box",
            cursor: "pointer",
            animation:
              phase === "shaking"
                ? source === "shake"
                  ? SHAKE_BALL_ANIMATION
                  : TAP_BALL_ANIMATION
                : "none",
          }}
        >
          <div
            style={{
              width: "194px",
              height: "194px",
              borderRadius: "50%",
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%,-50%)",
              border: "4px solid #3a3245",
              overflow: "hidden",
              boxSizing: "border-box",
            }}
          >
            <div
              style={{
                position: "absolute",
                inset: 0,
                transition: "opacity 0.15s",
                opacity: phase === "idle" ? 1 : 0,
              }}
            >
              <img
                src={norrisMagoImg}
                alt="Norris el magito"
                style={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  width: "124%",
                  height: "124%",
                  transform: "translate(-50%,-50%)",
                  objectFit: "cover",
                  display: "block",
                }}
              />
            </div>

            <div
              style={{
                position: "absolute",
                inset: 0,
                background: "#0e0b1c",
                opacity: phase === "idle" ? 0 : 1,
                transition: "opacity 0.2s",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  top: "29px",
                  left: "35px",
                  width: "37px",
                  height: "19px",
                  background: "rgba(255,255,255,0.14)",
                  borderRadius: "50%",
                  pointerEvents: "none",
                  animation:
                    phase === "shaking"
                      ? "bubbleswirl 1s ease-in-out infinite"
                      : "none",
                }}
              />
              <svg
                width="194"
                height="194"
                viewBox="0 0 124 124"
                aria-hidden="true"
                style={{
                  opacity: phase === "answer" ? 1 : 0,
                  transform: phase === "answer" ? "scale(1)" : "scale(0.85)",
                  transition: "opacity 0.4s, transform 0.4s",
                  transformOrigin: "62px 62px",
                }}
              >
                {geometry && (
                  <polygon
                    points={geometry.points}
                    fill="#3d5ce8"
                    stroke="#3d5ce8"
                    strokeWidth="4"
                    strokeLinejoin="round"
                  />
                )}
                {geometry &&
                  lines.map((line, i) => (
                    <text
                      key={i}
                      x="62"
                      y={geometry.ys[i]}
                      textAnchor="middle"
                      fontSize="8"
                      fontWeight="400"
                      letterSpacing="0.15"
                      fill="#eaf4ff"
                    >
                      {line}
                    </text>
                  ))}
              </svg>
            </div>
          </div>
        </div>
      </div>

      <p style={{ fontSize: "16px", color: "#8b7ab0", margin: "0 0 10px" }}>
        {phase === "idle" && "Toca la bola o agita tu cel."}
        {phase === "shaking" && "agitando..."}
        {phase === "answer" && "¿le atinó? · toca de nuevo"}
      </p>

      {iosPermission === "idle" && (
        <button
          onClick={requestIOSPermission}
          style={{
            marginBottom: "10px",
            background: "#4a3a80",
            border: "0.5px solid #7c66c9",
            color: "#fff",
            borderRadius: "20px",
            padding: "8px 22px",
            fontSize: "12px",
            fontWeight: 600,
          }}
        >
          activar sensor para agitar
        </button>
      )}
      {iosPermission === "asking" && (
        <p
          style={{
            fontSize: "11px",
            color: "#c9bdf0",
            margin: "0 0 10px",
          }}
        >
          acepta el permiso arriba 👆
        </p>
      )}

      <style>{`
        @keyframes ballshake {
          0%, 100% { transform: rotate(0deg); }
          20% { transform: rotate(-10deg); }
          40% { transform: rotate(8deg); }
          60% { transform: rotate(-6deg); }
          80% { transform: rotate(6deg); }
        }
        @keyframes bubbleswirl {
          0% { transform: translate(0,0) rotate(-15deg); }
          25% { transform: translate(5px,3px) rotate(4deg); }
          50% { transform: translate(-2px,6px) rotate(-10deg); }
          75% { transform: translate(-5px,1px) rotate(10deg); }
          100% { transform: translate(0,0) rotate(-15deg); }
        }
      `}</style>
    </div>
  );
};

export default MagicBall;
